import React from 'react';
import { Card, Col, Empty, Row, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { AdPlacementRow, MonetizationSummary } from '@/types';
import { fmtNum, fmtPct, fmtUsd } from '../bq-insights/bqMath';
import { rate, sumOf } from './monetizationMath';
import type { TypedEntry } from './monetizationTabState';
import { SummaryTile } from './SummaryTab';
import { TILES } from './tileSpecs';
import { titleWithInfo, totalRow } from './tableHelpers';
import { TabBody } from './TabBody';

const { Text } = Typography;

export const NO_SUMMARY_HINT =
  'Impressions per DAU comes from the Summary tab; open it for this range.';

/**
 * The tile's tooltip on this tab: the Summary tile's text plus where the number comes from. The
 * Summary tab's totals cover all countries, so the Country filter does not change this tile.
 */
export const ADS_TILE_INFO = `${TILES.find((t) => t.key === 'adImpressionsPerDau')?.info ?? ''} Covers all countries (the Country filter does not apply) and is read from the Summary tab.`;

const columns: ColumnsType<AdPlacementRow> = [
  { title: 'Placement', dataIndex: 'placement' },
  { title: 'Requests', dataIndex: 'requests', align: 'right', render: (v) => fmtNum(v) },
  { title: 'Impressions', dataIndex: 'impressions', align: 'right', render: (v) => fmtNum(v) },
  { title: 'Fails', dataIndex: 'fails', align: 'right', render: (v) => fmtNum(v) },
  { title: 'Rewards', dataIndex: 'rewards', align: 'right', render: (v) => fmtNum(v) },
  {
    title: titleWithInfo('Fail rate', 'Fails / requests'),
    align: 'right',
    render: (_, r) => fmtPct(rate(r.fails, r.requests)),
  },
  { title: 'Revenue', dataIndex: 'revenueUsd', align: 'right', render: (v) => fmtUsd(v) },
  {
    title: titleWithInfo('eCPM', 'Revenue per 1,000 impressions (USD)'),
    dataIndex: 'ecpmUsd',
    align: 'right',
    render: (v: number | null) => fmtUsd(v),
  },
];

const Content: React.FC<{ rows: AdPlacementRow[] }> = ({ rows }) => {
  if (rows.length === 0) {
    return <Empty description="No ad events in this range" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }
  const impressions = sumOf(rows, (r) => r.impressions);
  const revenue = sumOf(rows, (r) => r.revenueUsd);
  const ecpm = rate(revenue, impressions);
  return (
    <Table<AdPlacementRow>
      size="small"
      rowKey="placement"
      columns={columns}
      dataSource={rows}
      pagination={false}
      scroll={{ x: 'max-content' }}
      summary={() =>
        totalRow([
          null,
          fmtNum(sumOf(rows, (r) => r.requests)),
          fmtNum(impressions),
          fmtNum(sumOf(rows, (r) => r.fails)),
          fmtNum(sumOf(rows, (r) => r.rewards)),
          fmtPct(
            rate(
              sumOf(rows, (r) => r.fails),
              sumOf(rows, (r) => r.requests)
            )
          ),
          fmtUsd(revenue),
          fmtUsd(ecpm === null ? null : ecpm * 1000),
        ])
      }
    />
  );
};

interface Props {
  entry: TypedEntry<AdPlacementRow[]>;
  /** The Summary tab's entry: it holds the DAU that impressions per DAU needs. */
  summary: TypedEntry<MonetizationSummary>;
  onRetry?: () => void;
}

/** Ads per placement with eCPM, and the impressions per DAU tile against its target. */
export const AdsTab: React.FC<Props> = ({ entry, summary, onRetry }) => {
  const totals = summary.status === 'loaded' ? (summary.result.data?.totals ?? null) : null;
  return (
    <div>
      <Row gutter={[12, 12]} style={{ marginBottom: 12 }} data-section="ads-tile">
        <Col xs={24} sm={12} xl={6}>
          <SummaryTile id="adImpressionsPerDau" totals={totals} info={ADS_TILE_INFO} />
        </Col>
        {totals === null && (
          <Col xs={24} sm={12} xl={18}>
            <Text type="secondary" data-note="no-summary">
              {NO_SUMMARY_HINT}
            </Text>
          </Col>
        )}
      </Row>
      <Card size="small" title="By placement">
        <TabBody entry={entry} onRetry={onRetry}>
          {(rows) => <Content rows={rows} />}
        </TabBody>
      </Card>
    </div>
  );
};
